/**
 * Order tracker — delivery address update (inline in .order-layout).
 * Optional Google Places (Maps JS + Places) on address search, or HTTPS proxy lookup/details.
 */

const REQUEST_TIMEOUT_MS = 12000;
const DEBOUNCE_MS = 360;
const GOOGLE_MAPS_WAIT_MS = 12000;

const COUNTRY_LABELS = {
  GB: 'United Kingdom',
  JE: 'Jersey',
  GG: 'Guernsey',
  IM: 'Isle of Man',
  IE: 'Ireland',
  FR: 'France'
};

/** @type {object | null} */
let orderContext = null;
/** @type {{ fulfillmentDocumentNo: string | null } | null} */
let addressUpdateTarget = null;
let selectedPlaceId = null;
let addressLookupTimer = null;
let listenersBound = false;

/** Lazily created Maps AutocompleteService + PlacesService (see getPlacesServices). */
let placesServicesCache = null;

/** Parsed from theme settings (`data-address-update-countries`). */
let allowedCountryCodesCache = null;

/** Baseline form values when the address panel opens; used to detect unsaved edits. */
let initialFormSnapshot = null;

/** @type {'order' | 'spareParts' | 'reportIssue' | null} */
let addressPanelMode = null;

function isEmbeddedAddressMode(mode = addressPanelMode) {
  return mode === 'spareParts' || mode === 'reportIssue';
}

/** Original parent of `#address-update-panel` so embed mode can move it back. */
let addressPanelHome = null;

function tr() {
  return window.orderTrackerAddressUpdateTranslations || {};
}

function getTrackingRoot() {
  return document.getElementById('order-tracking');
}

function getOrderLayout() {
  return document.querySelector('#order-tracking .order-layout');
}

function getAddressPanel() {
  return document.getElementById('address-update-panel');
}

function isGooglePlacesConfigured() {
  return getTrackingRoot()?.dataset?.googleMapsPlaces === 'true';
}

function getDatasetUrls() {
  const el = getTrackingRoot();
  if (!el) {
    return { submit: '' };
  }
  const asHttps = (v) => {
    const t = String(v || '').trim();
    if (!t.toLowerCase().startsWith('https://')) return '';
    return t;
  };
  return {
    submit: asHttps(el.dataset.orderAmendApiUrl),
  };
}

function getClientApiKey() {
  return document.querySelector('#apiKeyContainer')?.dataset?.apiKey || '';
}

function buildSubmitUrl(apiKey) {
  const base = getDatasetUrls().submit;
  try {
    const url = new URL(base);
    url.searchParams.set('clientApiKey', apiKey);
    return url.toString();
  } catch {
    return '';
  }
}

function buildShippingAddressPayload(snap) {
  const country = normalizeCountryCode(snap.country);
  return {
    addressee: getAddresseeFromSnapshot(snap),
    phone: formatPhoneForSubmit(country, snap.phone),
    line1: snap.line1,
    line2: snap.line2 || '',
    city: snap.city,
    state: snap.state || '',
    zip: formatPostalCode(country, snap.postal) || snap.postal,
    country,
    attention: snap.attention,
  };
}

function getShipToPayload(snap = getFormSnapshot()) {
  if (!snap || typeof snap !== 'object') return null;
  const country = normalizeCountryCode(snap.country);
  return {
    addressee: getAddresseeFromSnapshot(snap),
    phone: formatPhoneForSubmit(country, snap.phone),
    line1: snap.line1 || '',
    line2: snap.line2 || '',
    town: snap.city || '',
    county: snap.state || '',
    postcode: formatPostalCode(country, snap.postal) || snap.postal || '',
    countryCode: country,
  };
}

const COUNTRY_NAME_TO_CODE = {
  'united kingdom': 'GB',
  uk: 'GB',
  'great britain': 'GB',
  im: 'IM',
  gg: 'GG',
  je: 'JE',
  fr: 'FR',
  'france': 'FR',
  'ireland': 'IE',
  'isle of man': 'IM',
  'guernsey': 'GG',
  'jersey': 'JE',
};

function getAllowedCountryCodes() {
  if (allowedCountryCodesCache) return allowedCountryCodesCache;
  const raw = getTrackingRoot()?.dataset?.addressUpdateCountries || '';
  const parsed = String(raw)
    .split(/[,;\s]+/)
    .map((c) => c.trim().toUpperCase())
    .filter((c) => /^[A-Z]{2}$/.test(c));
  const unique = [...new Set(parsed)];
  allowedCountryCodesCache = unique.length ? unique : ['GB'];

  return allowedCountryCodesCache;
}

function getDefaultCountryCode() {
  return getAllowedCountryCodes()[0];
}

function resolveCountryCode(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (/^[a-zA-Z]{2}$/.test(s)) return s.toUpperCase();
  const lower = s.toLowerCase().replace(/\./g, '');
  return COUNTRY_NAME_TO_CODE[lower] || null;
}

function isAllowedCountryCode(raw) {
  const code = resolveCountryCode(raw);
  return !!code && getAllowedCountryCodes().includes(code);
}

function normalizeCountryCode(raw) {
  const code = resolveCountryCode(raw);
  if (code && getAllowedCountryCodes().includes(code)) return code;
  return getDefaultCountryCode();
}

function allowedCountryEntries() {
  return getAllowedCountryCodes()
    .map((code) => ({ code, label: getCountryLabel(code) }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

/** Up to five ISO codes for Google `componentRestrictions.country` (all allowed, not selected only). */
function googlePlacesCountryRestriction() {
  const allowed = getAllowedCountryCodes();
  const selected = (getById('address-update-country-select')?.value || '').toUpperCase();
  const codes = allowed.slice(0, 5).map((c) => c.toLowerCase());
  const restriction = codes.length === 1 ? codes[0] : codes;

  return restriction;
}

/** @param {string} id */
function getById(id) {
  return document.getElementById(id);
}

function showEl(el, show) {
  if (!el) return;
  el.classList.toggle('hidden', !show);
}

function hasEditableShipments(orderData) {
  const oi = orderData?.orderDetails?.orderItems;
  return (
    (oi?.preparingForDespatchShipments?.length > 0) ||
    (oi?.outstandingBatches?.length > 0)
  );
}

function findEditableShipmentByDocumentNo(orderData, documentNo) {
  const oi = orderData?.orderDetails?.orderItems;
  const doc = String(documentNo || '').trim();
  if (!oi || !doc) return null;
  const batches = [
    ...(oi.preparingForDespatchShipments || []),
    ...(oi.outstandingBatches || []),
  ];
  return batches.find((batch) => String(batch?.documentNo || '').trim() === doc) || null;
}

function getAddressSourceForForm() {
  const orderShip = orderContext?.orderDetails?.shippingAddress || {};
  const docNo = addressUpdateTarget?.fulfillmentDocumentNo;
  if (!docNo) return orderShip;
  const batch = findEditableShipmentByDocumentNo(orderContext, docNo);
  return batch?.shippingAddress || orderShip;
}

function updateAddressUpdateLaunchVisibility(orderData) {
  const wrap = getById('address-update-launch-wrap');
  if (!wrap) return;
  if (!orderData || !hasEditableShipments(orderData)) {
    wrap.classList.add('hidden');
    return;
  }
  wrap.classList.remove('hidden');
}

function setAddressUpdateScopeLabel() {
  const el = getById('address-update-scope-label');
  if (!el) return;
  const docNo = addressUpdateTarget?.fulfillmentDocumentNo;

  if (window.innerWidth >= 750) {
    if (!docNo) {
      el.querySelector('span').textContent = '';
      showEl(el, false);
      return;
    }
    const template =
      window.orderTrackerTranslations?.addressUpdateScopeShipmentTemplate ||
      'Updating delivery address for shipment {{ shipment_id }}';
    el.querySelector('span').textContent = template.replace(/\{\{\s*shipment_id\s*\}\}/g, docNo);
    showEl(el, true);
  } else {
    showEl(el, true);
  }
}

function splitAddresseeName(fullName) {
  const parts = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function getAddresseeFromSnapshot(snap) {
  return [snap.firstName, snap.lastName].filter(Boolean).join(' ').trim();
}

function getFormSnapshot() {
  return {
    country: normalizeCountryCode(getById('address-update-country-select')?.value || getDefaultCountryCode()),
    firstName: (getById('address-update-first-name')?.value || '').trim(),
    lastName: (getById('address-update-last-name')?.value || '').trim(),
    phone: (getById('address-update-phone')?.value || '').trim(),
    email: (getById('address-update-email')?.value || '').trim(),
    line1: (getById('address-update-line1')?.value || '').trim(),
    line2: (getById('address-update-line2')?.value || '').trim(),
    city: (getById('address-update-city')?.value || '').trim(),
    state: (getById('address-update-state')?.value || '').trim(),
    postal: (getById('address-update-postal')?.value || '').trim(),
    attention: (getById('address-update-attention')?.value || '').trim(),
  };
}

const FORM_SNAPSHOT_KEYS = [
  'country',
  'firstName',
  'lastName',
  'phone',
  'email',
  'line1',
  'line2',
  'city',
  'state',
  'postal',
  'attention',
];

function formSnapshotsEqual(a, b) {
  if (!a || !b) return true;
  return FORM_SNAPSHOT_KEYS.every((key) => (a[key] || '') === (b[key] || ''));
}

function hasFormChanges() {
  if (!initialFormSnapshot) return false;
  return !formSnapshotsEqual(initialFormSnapshot, getFormSnapshot());
}

function setCountry(iso) {
  const code = normalizeCountryCode(iso);
  const sel = getById('address-update-country-select');
  if (sel && [...sel.options].some((o) => o.value === code)) {
    sel.value = code;
  } else if (sel) {
    sel.value = getDefaultCountryCode();
  }
}

const VALIDATION_FIELD_ORDER = ['phone', 'line1', 'city', 'state', 'postal'];

const FIELD_INPUT_IDS = {
  phone: 'address-update-phone',
  line1: 'address-update-line1',
  city: 'address-update-city',
  state: 'address-update-state',
  postal: 'address-update-postal',
};

function getFieldInput(field) {
  const inputId = FIELD_INPUT_IDS[field];
  return inputId ? getById(inputId) : null;
}

function clearFieldErrors() {
  VALIDATION_FIELD_ORDER.forEach((key) => {
    const err = getById(`address-update-${key === 'line1' ? 'line1' : key}-error`);
    const input = getFieldInput(key);
    showEl(err, false);
    if (err) err.textContent = '';
    input?.classList.remove('is-invalid');
  });
}

function setFieldError(field, message) {
  const errId = `address-update-${field === 'line1' ? 'line1' : field}-error`;
  const input = getFieldInput(field);
  const err = getById(errId);
  if (err) {
    err.textContent = message;
    showEl(err, true);
  }
  input?.classList.add('is-invalid');
}

function focusFirstInvalidField() {
  for (const field of VALIDATION_FIELD_ORDER) {
    const input = getFieldInput(field);
    if (!input?.classList.contains('is-invalid')) continue;
    scrollElementIntoViewWithOffset(input, { extraPadding: 12 });
    try {
      input.focus({ preventScroll: true });
    } catch {
      input.focus();
    }
    return;
  }
}

const POSTAL_PATTERNS = {
  GB: /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/,
  IM: /^IM[0-9] ?[0-9][A-Za-z]{2}$/i,
  JE: /^JE[0-9] ?[0-9][A-Za-z]{2}$/i,
  GG: /^GY[0-9] ?[0-9][A-Za-z]{2}$/i,
  IE: /^([AC-FHKNPRTV-Y][0-9]{2}|D6W)\s?[0-9AC-FHKNPRTV-Y]{4}$/i,
  FR: /^[0-9]{5}$/,
};

const PHONE_DIAL_CODES = {
  GB: '+44',
  IM: '+44',
  JE: '+44',
  GG: '+44',
  IE: '+353',
  FR: '+33',
};

function getPhoneDialCode(country) {
  return PHONE_DIAL_CODES[normalizeCountryCode(country)] || '+44';
}

function getPhoneDialDigits(country) {
  return getPhoneDialCode(country).replace('+', '');
}

function updatePhonePrefix(country) {
  const el =
    getById('address-update-phone-prefix') ||
    getById('address-update-phone')
      ?.closest('.address-update__input-wrap')
      ?.querySelector('.address-update__input-prefix');
  if (el) el.textContent = getPhoneDialCode(country);
}

function stripPhoneDialCode(country, phone) {
  let digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';

  const dialDigits = getPhoneDialDigits(country);
  if (digits.startsWith(dialDigits)) {
    digits = digits.slice(dialDigits.length);
  }

  if (digits && digits[0] !== '0') {
    digits = `0${digits}`;
  }

  return digits;
}

function normalizePhoneDigits(country, phone) {
  let digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';

  const group = getPhoneCountryGroup(country);
  if (group === 'uk') {
    if (digits[0] !== '0') digits = `0${digits}`;
    return digits.slice(0, 11);
  }
  if (group === 'ie') {
    if (digits[0] !== '0') digits = `0${digits}`;
    return digits.slice(0, 10);
  }
  if (group === 'fr') {
    if (digits[0] !== '0') digits = `0${digits}`;
    return digits.slice(0, 10);
  }

  return digits.slice(0, 15);
}

function isValidPhone(country, phone) {
  const digits = normalizePhoneDigits(country, phone);
  if (!digits) return false;

  if (['GB', 'IM', 'JE', 'GG'].includes(country)) {
    return /^0\d{9,10}$/.test(digits);
  }
  if (country === 'IE') {
    return /^0\d{8,9}$/.test(digits);
  }
  if (country === 'FR') {
    return /^0[1-9]\d{8}$/.test(digits);
  }

  return digits.length >= 8;
}

function getPhoneCountryGroup(country) {
  if (['GB', 'IM', 'JE', 'GG'].includes(country)) return 'uk';
  if (country === 'IE') return 'ie';
  if (country === 'FR') return 'fr';
  return 'generic';
}

function extractPhoneDigitsForMask(country, phone) {
  return normalizePhoneDigits(country, phone);
}

function applyPhoneMask(country, phone) {
  const digits = extractPhoneDigitsForMask(country, phone);
  if (!digits) return '';

  const group = getPhoneCountryGroup(country);
  if (group === 'uk') {
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  if (group === 'ie') {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  if (group === 'fr') {
    if (digits.length <= 2) return digits;
    const parts = [digits.slice(0, 2)];
    const rest = digits.slice(2);
    for (let i = 0; i < rest.length; i += 2) {
      parts.push(rest.slice(i, i + 2));
    }
    return parts.join(' ');
  }

  return digits;
}

function applyPhoneMaskToInput(input) {
  if (!(input instanceof HTMLInputElement)) return;
  const country = normalizeCountryCode(getById('address-update-country-select')?.value || getDefaultCountryCode());
  const prevValue = input.value;
  const selectionStart = input.selectionStart ?? prevValue.length;
  const digitsBeforeCursor = prevValue.slice(0, selectionStart).replace(/\D/g, '').length;
  const masked = applyPhoneMask(country, prevValue);
  if (masked === prevValue) return;

  input.value = masked;

  let cursor = 0;
  let digitsSeen = 0;
  while (cursor < masked.length && digitsSeen < digitsBeforeCursor) {
    if (/\d/.test(masked[cursor])) digitsSeen += 1;
    cursor += 1;
  }
  input.setSelectionRange(cursor, cursor);
}

function formatPhoneForSubmit(country, localPhone) {
  const dial = getPhoneDialCode(country);
  const digits = normalizePhoneDigits(country, localPhone);
  if (!digits) return '';
  const national = digits.startsWith('0') ? digits.slice(1) : digits;
  const maskedLocal = applyPhoneMask(country, `0${national}`);
  const formattedNational = maskedLocal.replace(/^0\s?/, '').trim();
  return `${dial} ${formattedNational}`.trim();
}

function formatPostalCode(country, postal) {
  let value = String(postal || '').trim().toUpperCase();
  if (!value) return '';

  if (['GB', 'IM', 'JE', 'GG'].includes(country) && !value.includes(' ')) {
    value = value.replace(/^(.+?)(\d[A-Z]{2})$/i, '$1 $2');
  } else if (country === 'IE' && !value.includes(' ')) {
    value = value.replace(/^(.{3})(.{4})$/i, '$1 $2');
  }

  return value;
}

function isValidPostalCode(country, postal) {
  const formatted = formatPostalCode(country, postal);
  if (!formatted) return false;
  const pattern = POSTAL_PATTERNS[normalizeCountryCode(country)];
  return pattern ? pattern.test(formatted) : formatted.length >= 2;
}

function validateForm() {
  clearFieldErrors();
  const snap = getFormSnapshot();
  let ok = true;

  if (!snap.phone) {
    setFieldError('phone', tr().errorRequired || 'Required');
    ok = false;
  } else if (!isValidPhone(snap.country, snap.phone)) {
    setFieldError('phone', tr().errorPhone || 'Invalid phone number');
    ok = false;
  }
  if (!snap.line1) {
    setFieldError('line1', tr().errorLine1 || 'Required');
    ok = false;
  }
  if (!snap.city) {
    setFieldError('city', tr().errorRequired || 'Required');
    ok = false;
  }
  if (!snap.state) {
    setFieldError('state', tr().errorRequired || 'Required');
    ok = false;
  }
  if (!snap.postal) {
    setFieldError('postal', tr().errorRequired || 'Required');
    ok = false;
  } else if (!isValidPostalCode(snap.country, snap.postal)) {
    setFieldError('postal', tr().errorPostal || 'Invalid postcode');
    ok = false;
  }
  if (!ok) focusFirstInvalidField();
  return ok;
}

function showStep(step) {
  const panel = getAddressPanel();
  if (!panel) return;
  panel.querySelectorAll('[data-address-step]').forEach((node) => {
    showEl(node, node.getAttribute('data-address-step') === step);
  });
}

function replaceTemplateToken(text, token, value) {
  const re = new RegExp(`\\{\\{\\s*${token}\\s*\\}\\}`, 'g');
  return String(text || '').replace(re, value);
}

let regionDisplayNames = null;

function getRegionDisplayNames() {
  if (regionDisplayNames !== null) return regionDisplayNames;
  try {
    regionDisplayNames = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    regionDisplayNames = undefined;
  }
  return regionDisplayNames;
}

function getCountryLabel(code) {
  const resolved = resolveCountryCode(code) || String(code || '').trim().toUpperCase();
  if (!resolved) return '';
  if (COUNTRY_LABELS[resolved]) return COUNTRY_LABELS[resolved];
  const display = getRegionDisplayNames()?.of(resolved);
  if (display && display !== resolved) return display;
  return resolved;
}

function formatAddressSnapshotLines(snap) {
  if (!snap) return [];
  const lines = [];
  if (snap.line1) lines.push(snap.line1);
  if (snap.line2) lines.push(snap.line2);
  if (snap.city) lines.push(snap.city);
  if (snap.state) lines.push(snap.state);
  if (snap.postal) lines.push(snap.postal);
  const countryLabel = getCountryLabel(snap.country);
  if (countryLabel) lines.push(countryLabel);
  return lines;
}

function renderAddressLines(el, snap) {
  if (!el) return;
  el.innerHTML = '';
  formatAddressSnapshotLines(snap).forEach((line) => {
    const span = document.createElement('span');
    span.textContent = line;
    el.appendChild(span);
  });
}

function setSuccessStepContent(snap) {
  const headlineEl = getById('address-update-success-headline');
  const noticeEl = getById('address-update-success-notice');
  const addressEl = getById('address-update-success-address');
  const docNo = addressUpdateTarget?.fulfillmentDocumentNo;
  const translations = tr();

  renderAddressLines(addressEl, snap);

  if (docNo) {
    if (headlineEl) {
      headlineEl.textContent = replaceTemplateToken(
        translations.successHeadlineShipment ||
          'Your shipping address for {{ doc_no }} has been updated',
        'doc_no',
        docNo
      );
    }
    if (noticeEl) {
      noticeEl.textContent =
        translations.successNoticeShipment ||
        'Any other existing or future shipments will not be affected';
    }
    return;
  }

  if (headlineEl) {
    headlineEl.textContent =
      translations.successHeadlineOrder ||
      'Your shipping address for future shipments has been updated';
  }
  if (noticeEl) {
    noticeEl.textContent =
      translations.successNoticeOrder || 'Your existing shipments have not been affected';
  }
}

function setGlobalError(msg) {
  const el = getById('address-update-global-error');
  if (!el) return;
  el.textContent = msg || '';
  showEl(el, !!msg);
}

function getStickyTopOffset(extraPadding = 12) {
  let offset = extraPadding;

  document.querySelectorAll('.shoplift-offer-bar-sticky, sticky-header, .shopify-section-header-sticky').forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.height > 0 && rect.bottom > 0) {
      offset = Math.max(offset, rect.bottom + extraPadding);
    }
  });

  if (offset <= extraPadding) {
    const stickyHeader = document.querySelector('sticky-header');
    const fallbackHeader = document.querySelector('.header-wrapper, header');
    offset =
      (stickyHeader?.offsetHeight || fallbackHeader?.offsetHeight || 0) + extraPadding;
  }

  if (window.innerWidth < 768) offset += 16;
  return offset;
}

function isElementVisibleWithOffset(el, extraPadding = 12) {
  if (!el) return true;
  const rect = el.getBoundingClientRect();
  const topBound = getStickyTopOffset(extraPadding);
  const bottomBound = window.innerHeight - extraPadding;
  return rect.top >= topBound && rect.bottom <= bottomBound;
}

function scrollElementIntoViewWithOffset(el, options = {}) {
  if (!el) return;
  const { behavior = 'smooth', extraPadding = 12 } = options;
  if (isElementVisibleWithOffset(el, extraPadding)) return;
  const offset = getStickyTopOffset(extraPadding);
  const top =
    el.getBoundingClientRect().top +
    window.pageYOffset -
    offset;
  const preferReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  window.scrollTo({
    top: Math.max(0, Math.min(top, document.documentElement.scrollHeight - window.innerHeight)),
    behavior: preferReducedMotion ? 'auto' : behavior,
  });
}

function focusAddressSearchField() {
  const input = getById('address-update-address-search');
  if (!input) return;
  scrollElementIntoViewWithOffset(input, { extraPadding: 12 });
  try {
    input.focus({ preventScroll: true });
  } catch {
    input.focus();
  }
}

function scrollSuccessStepIntoView() {
  const step =
    getAddressPanel()?.querySelector('[data-address-step="success"]') ||
    getById('address-update-success-headline');
  scrollElementIntoViewWithOffset(step, { extraPadding: 12 });
}

function rememberAddressPanelHome(panel) {
  if (addressPanelHome || !panel?.parentNode) return;
  addressPanelHome = { parent: panel.parentNode, next: panel.nextSibling };
}

function restoreAddressPanelHome() {
  const panel = getAddressPanel();
  if (!panel || !addressPanelHome?.parent) return;
  if (panel.parentNode === addressPanelHome.parent) return;
  if (addressPanelHome.next && addressPanelHome.next.parentNode === addressPanelHome.parent) {
    addressPanelHome.parent.insertBefore(panel, addressPanelHome.next);
  } else {
    addressPanelHome.parent.appendChild(panel);
  }
}

/**
 * @param {{ fulfillmentDocumentNo?: string, mode?: 'spareParts' | 'reportIssue', snapshot?: object|null, mountEl?: HTMLElement }} options
 * @returns {boolean}
 */
function showAddressPanel(options = {}) {
  const orderLayout = getOrderLayout();
  const panel = getAddressPanel();
  if (!orderLayout || !panel) return false;
  const isEmbed = isEmbeddedAddressMode(options.mode);
  if (!isEmbed && !orderContext) return false;

  if (!isEmbed) {
    const docNo = options.fulfillmentDocumentNo
      ? String(options.fulfillmentDocumentNo).trim()
      : null;
    if (docNo) {
      if (!findEditableShipmentByDocumentNo(orderContext, docNo)) return false;
    } else if (!hasEditableShipments(orderContext)) {
      return false;
    }
    addressUpdateTarget = { fulfillmentDocumentNo: docNo || null };
  } else {
    addressUpdateTarget = { fulfillmentDocumentNo: null };
  }

  rememberAddressPanelHome(panel);
  if (isEmbed && options.mountEl instanceof HTMLElement) {
    options.mountEl.appendChild(panel);
    panel.classList.add('address-update-panel--embed');
    addressPanelMode = options.mode === 'spareParts' ? 'spareParts' : 'reportIssue';
  } else {
    restoreAddressPanelHome();
    panel.classList.remove('address-update-panel--embed');
    addressPanelMode = 'order';
  }

  clearFieldErrors();
  setGlobalError('');
  hideSuggestLists();
  if (isEmbed && options.snapshot && typeof options.snapshot === 'object') {
    applyFormSnapshot(options.snapshot);
  } else {
    populateFormFromOrder();
  }
  setAddressUpdateScopeLabel();
  showStep('form');

  if (!isEmbed) {
    orderLayout.classList.add('change-address-active');
  }
  panel.classList.remove('hidden');
  panel.setAttribute('aria-hidden', 'false');

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      focusAddressSearchField();
    });
  });
  return true;
}

function hideAddressPanel() {
  const orderLayout = getOrderLayout();
  const panel = getAddressPanel();
  if (orderLayout) orderLayout.classList.remove('change-address-active');
  if (panel) {
    panel.classList.add('hidden');
    panel.classList.remove('address-update-panel--embed');
    panel.setAttribute('aria-hidden', 'true');
  }
  restoreAddressPanelHome();
  hideSuggestLists();
  showStep('form');
  setGlobalError('');
  addressUpdateTarget = null;
  addressPanelMode = null;
  initialFormSnapshot = null;
  setAddressUpdateScopeLabel();
}

/** Close cancel confirmation (if open) and exit address edit without saving. */
function discardChangeDeliveryAddressChanges() {
  getById('change-delivery-address-discard-changes')
    ?.closest('.notification-box')
    ?.classList.remove('notification-box--active');
  hideAddressPanel();
}

async function fetchWithTimeout(url, options = {}, timeoutMs = REQUEST_TIMEOUT_MS) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...options, signal: ctrl.signal });
    return res;
  } finally {
    clearTimeout(t);
  }
}

async function readJsonBody(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function normalizeSubmitResponse(data) {
  if (!data || typeof data !== 'object') return null;
  return data.data && typeof data.data === 'object' ? data.data : data;
}

function getSubmitErrorMessage(payload) {
  if (!payload || typeof payload !== 'object') return '';
  return String(payload.errorMessage || payload.message || '').trim();
}

function isTechnicalSubmitError(rawMessage) {
  const trimmed = String(rawMessage || '').trim();
  if (!trimmed.startsWith('{')) return false;
  try {
    const parsed = JSON.parse(trimmed);
    return (
      parsed?.type === 'error.SuiteScriptError' ||
      Array.isArray(parsed?.stack) ||
      (parsed?.cause && typeof parsed.cause === 'object')
    );
  } catch {
    return false;
  }
}

function resolveSubmitErrorMessage(payload) {
  const raw = getSubmitErrorMessage(payload);
  if (!raw) return tr().errorSubmit || 'Error';
  if (isTechnicalSubmitError(raw)) {
    console.error('Address update submit error:', raw);
    return tr().errorSubmit || 'Error';
  }
  return raw;
}

function normalizeLookupResults(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.predictions)) return data.predictions;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.suggestions)) return data.suggestions;
  return [];
}

function predictionPlaceId(p) {
  return p.place_id || p.placeId || p.id || '';
}

function predictionDescription(p) {
  return p.description || p.formatted_address || p.label || '';
}

async function waitForGoogleMaps(maxMs = GOOGLE_MAPS_WAIT_MS) {
  if (!isGooglePlacesConfigured()) return false;
  if (window.google?.maps?.places) return true;
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    if (window.google?.maps?.places) return true;
    await new Promise((r) => setTimeout(r, 80));
  }
  return false;
}

function getPlacesServices() {
  if (placesServicesCache) return placesServicesCache;
  if (!window.google?.maps?.places) return null;

  let host = getById('address-update-places-map-host');
  if (!host) {
    host = document.createElement('div');
    host.id = 'address-update-places-map-host';
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText =
      'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;overflow:hidden;top:0;left:0;';
    (getAddressPanel() || document.body).appendChild(host);
  }

  const map = new google.maps.Map(host, {
    center: { lat: 51.5, lng: -0.12 },
    zoom: 10,
    disableDefaultUI: true,
    gestureHandling: 'none',
  });

  placesServicesCache = {
    autocomplete: new google.maps.places.AutocompleteService(),
    places: new google.maps.places.PlacesService(map),
  };
  return placesServicesCache;
}

function scheduleGooglePlacesWarmup() {
  if (!isGooglePlacesConfigured()) return;
  const runWarmup = () => {
    try {
      getPlacesServices();
    } catch (e) {
      console.error('Google Places warmup failed:', e);
    }
  };
  if (window.__orderTrackerPlacesReady && window.google?.maps?.places) {
    runWarmup();
    return;
  }
  if (typeof window.__orderTrackerPlacesInitQueue !== 'undefined') {
    window.__orderTrackerPlacesInitQueue.push(runWarmup);
  }
}

/** Map Google `getDetails` place result into the same shape as the proxy API for `applyDetailsToForm`. */
function mapGooglePlaceToDetail(place) {
  const comp = {};
  for (const c of place.address_components || []) {
    for (const t of c.types) {
      comp[t] = { long: c.long_name, short: c.short_name };
    }
  }
  const sn = comp.street_number?.long || '';
  const route = comp.route?.long || '';
  let line1 = [sn, route].filter(Boolean).join(' ').trim();
  if (!line1 && place.formatted_address) {
    line1 = String(place.formatted_address).split(',')[0].trim();
  }
  const line2 = comp.subpremise?.long || '';
  const city =
    comp.locality?.long ||
    comp.postal_town?.long ||
    comp.sublocality_level_1?.long ||
    comp.neighborhood?.long ||
    '';
  const state =
    comp.administrative_area_level_2?.long ||
    comp.administrative_area_level_1?.long ||
    '';
  const postal = comp.postal_code?.long || '';
  const country = comp.country?.short || '';
  return {
    address_line1: line1,
    address_line2: line2,
    city,
    state,
    postal_code: postal,
    country,
  };
}

function googlePlacePredictions(query) {
  return new Promise((resolve) => {
    const svc = getPlacesServices();
    if (!svc?.autocomplete) {
      resolve([]);
      return;
    }
    const country = googlePlacesCountryRestriction();
    svc.autocomplete.getPlacePredictions(
      {
        input: query,
        componentRestrictions: {
          country: country
        },
        region: 'gb',
      },
      (predictions, status) => {
        if (
          status !== google.maps.places.PlacesServiceStatus.OK ||
          !predictions ||
          !predictions.length
        ) {
          resolve([]);
          return;
        }
        resolve(
          predictions.map((p) => ({
            place_id: p.place_id,
            description: p.description,
          }))
        );
      }
    );
  });
}

function googlePlaceDetails(placeId) {
  return new Promise((resolve) => {
    const svc = getPlacesServices();
    if (!svc?.places) {
      resolve(null);
      return;
    }
    svc.places.getDetails(
      {
        placeId,
        fields: ['address_component', 'formatted_address', 'geometry'],
      },
      (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
          resolve(null);
          return;
        }
        resolve(mapGooglePlaceToDetail(place));
      }
    );
  });
}

async function runAddressLookupProxy(query) {
  const { lookup } = getDatasetUrls();
  if (!lookup || query.length < 2) return [];
  const country = (getById('address-update-country-select')?.value || getDefaultCountryCode()).toUpperCase();
  const res = await fetchWithTimeout(lookup, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ query, country }),
  });
  if (!res.ok) return [];
  const data = await readJsonBody(res);
  return normalizeLookupResults(data);
}

async function runAddressLookup(query) {
  if (query.length < 2) return [];

  if (isGooglePlacesConfigured()) {
    const ready = await waitForGoogleMaps();
    if (ready && window.google?.maps?.places) {
      try {
        getPlacesServices();
        const preds = await googlePlacePredictions(query);
        if (preds.length > 0) return preds;
      } catch (err) {
        console.error('Google Places autocomplete failed:', err);
      }
    }
  }

  return runAddressLookupProxy(query);
}

async function runAddressDetailsProxy(placeId) {
  const { details } = getDatasetUrls();
  if (!details || !placeId) return null;
  const res = await fetchWithTimeout(details, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ place_id: placeId }),
  });
  if (!res.ok) return null;
  return readJsonBody(res);
}

async function runAddressDetails(placeId) {
  if (!placeId) return null;

  if (isGooglePlacesConfigured()) {
    const ready = await waitForGoogleMaps(8000);
    if (ready && window.google?.maps?.places) {
      try {
        getPlacesServices();
        const detail = await googlePlaceDetails(placeId);
        if (detail) return detail;
      } catch (err) {
        console.error('Google Places getDetails failed:', err);
      }
    }
  }

  return runAddressDetailsProxy(placeId);
}

function applyDetailsToForm(detail) {
  if (!detail || typeof detail !== 'object') return false;
  const country = detail.country ?? detail.country_code ?? '';
  if (country && !isAllowedCountryCode(country)) {
    setGlobalError(tr().errorCountryNotAllowed || '');
    return false;
  }
  const line1 = detail.address_line1 ?? detail.address1 ?? detail.line1 ?? '';
  const line2 = detail.address_line2 ?? detail.address2 ?? detail.line2 ?? '';
  const city = detail.city ?? detail.locality ?? '';
  const state = detail.state ?? detail.region ?? detail.county ?? '';
  const postal = detail.postal_code ?? detail.postalCode ?? detail.zip ?? '';
  if (getById('address-update-line1')) getById('address-update-line1').value = String(line1);
  if (getById('address-update-line2')) getById('address-update-line2').value = String(line2);
  if (getById('address-update-city')) getById('address-update-city').value = String(city);
  if (getById('address-update-state')) getById('address-update-state').value = String(state);
  const countryCode = country ? normalizeCountryCode(String(country)) : normalizeCountryCode(getById('address-update-country-select')?.value || getDefaultCountryCode());
  if (country) setCountry(countryCode);
  if (getById('address-update-postal')) {
    getById('address-update-postal').value = formatPostalCode(countryCode, postal);
  }
  return true;
}

function renderSuggestList(ul, items, onPick) {
  if (!ul) return;
  ul.innerHTML = '';
  if (!items.length) {
    showEl(ul, false);
    return;
  }
  items.forEach((text, i) => {
    const li = document.createElement('li');
    li.setAttribute('role', 'option');
    li.tabIndex = -1;
    li.textContent = text;
    li.dataset.index = String(i);
    li.addEventListener('mousedown', (e) => {
      e.preventDefault();
      onPick(text, i);
    });
    ul.appendChild(li);
  });
  showEl(ul, true);
}

function hideSuggestLists() {
  const ul = getById('address-update-address-suggest');
  showEl(ul, false);
  if (ul) ul.innerHTML = '';
}

function buildSnapshotFromOrderData() {
  const ship = getAddressSourceForForm();
  const cust = orderContext?.customerInformation || {};
  const email = String(cust.email || '').trim();
  const phone = String(ship.shippingPhone || cust.phone || '').trim();
  const fromCust = {
    firstName: String(cust.firstName || '').trim(),
    lastName: String(cust.lastName || '').trim(),
  };

  const fromAddressee = splitAddresseeName(
    ship.addressee || cust.fullName || [fromCust.firstName, fromCust.lastName].filter(Boolean).join(' ')
  );

  return {
    country: normalizeCountryCode(ship.country || getDefaultCountryCode()),
    firstName: String(fromCust.firstName || fromAddressee.firstName).trim(),
    lastName: String(fromCust.lastName || fromAddressee.lastName).trim(),
    phone,
    email,
    line1: String(ship.address1 || '').trim(),
    line2: String(ship.address2 || '').trim(),
    city: String(ship.city || '').trim(),
    state: String(ship.state || '').trim(),
    postal: String(ship.zipCode || '').trim(),
    attention: String(ship.attention || '').trim(),
  };
}

function applyFormSnapshot(raw) {
  selectedPlaceId = null;
  placesServicesCache = null;
  const snap = raw && typeof raw === 'object' ? raw : {};
  if (getById('address-update-address-search')) getById('address-update-address-search').value = '';
  if (getById('address-update-first-name')) {
    getById('address-update-first-name').value = String(snap.firstName || '');
  }
  if (getById('address-update-last-name')) {
    getById('address-update-last-name').value = String(snap.lastName || '');
  }
  if (getById('address-update-line1')) getById('address-update-line1').value = String(snap.line1 || '');
  if (getById('address-update-line2')) getById('address-update-line2').value = String(snap.line2 || '');
  if (getById('address-update-city')) getById('address-update-city').value = String(snap.city || '');
  if (getById('address-update-state')) getById('address-update-state').value = String(snap.state || '');
  if (getById('address-update-attention')) {
    getById('address-update-attention').value = String(snap.attention || '');
  }
  if (getById('address-update-email')) getById('address-update-email').value = String(snap.email || '');
  setCountry(snap.country);
  const country = normalizeCountryCode(snap.country);
  updatePhonePrefix(country);
  if (getById('address-update-phone')) {
    getById('address-update-phone').value = applyPhoneMask(
      country,
      stripPhoneDialCode(country, snap.phone)
    );
  }
  if (getById('address-update-postal')) {
    getById('address-update-postal').value = formatPostalCode(country, snap.postal);
  }
  initialFormSnapshot = getFormSnapshot();
}

function populateFormFromOrder() {
  applyFormSnapshot(buildSnapshotFromOrderData());
}

function mapResponseToShippingAddress(payload, snap) {
  const prev = orderContext?.orderDetails?.shippingAddress || {};
  const raw = payload && typeof payload === 'object' ? payload : {};
  const p = raw.shippingAddress && typeof raw.shippingAddress === 'object' ? raw.shippingAddress : raw;
  const phoneFromPayload =
    p.phone != null
      ? String(p.phone)
      : p.shipping_phone != null
        ? String(p.shipping_phone)
        : p.shippingPhone != null
          ? String(p.shippingPhone)
          : '';
  return {
    addressee: String(p.addressee ?? getAddresseeFromSnapshot(snap) ?? prev.addressee ?? ''),
    address1: String(p.line1 ?? p.address_line1 ?? p.address1 ?? snap.line1),
    address2: String(p.line2 ?? p.address_line2 ?? p.address2 ?? snap.line2 ?? ''),
    attention: p.company_name != null ? String(p.company_name) : snap.attention ? String(snap.attention) : prev.attention ?? null,
    city: String(p.city ?? snap.city),
    state: String(p.state ?? p.region ?? snap.state),
    zipCode: String(p.zip ?? p.postal_code ?? p.postalCode ?? snap.postal),
    country: String(p.country ?? snap.country).toUpperCase(),
    shippingPhone: phoneFromPayload || (snap.phone ? String(snap.phone) : prev.shippingPhone ?? null),
  };
}

function getAddressUpdateCancelButtons() {
  return getAddressPanel()?.querySelectorAll('[data-confirmation-box="cancel-change-delivery-address"]') || [];
}

function setAddressUpdateCancelButtonsDisabled(disabled) {
  getAddressUpdateCancelButtons().forEach((button) => {
    button.disabled = disabled;
  });
}

async function submitUpdate() {
  if (isEmbeddedAddressMode()) return;
  const key = getClientApiKey();
  const snap = getFormSnapshot();
  const btn = getById('address-update-submit');
  const labelEl = btn?.querySelector('.address-update__submit-label');
  const progEl = btn?.querySelector('.address-update__submit-progress');
  const submitUrl = buildSubmitUrl(key);

  if (!key || !orderContext) {
    setGlobalError(window.orderTrackerTranslations?.errorConfigError || 'Configuration error');
    return;
  }
  if (!submitUrl) {
    setGlobalError(tr().errorNotConfigured || 'Address update is not configured');
    return;
  }

  const shippingAddress = buildShippingAddressPayload(snap);
  const fulfillmentDocumentNo = addressUpdateTarget?.fulfillmentDocumentNo;

  let body;

  if (fulfillmentDocumentNo) {
    const batch = findEditableShipmentByDocumentNo(orderContext, fulfillmentDocumentNo);
    const fulfillmentId = batch?.documentNo;
    if (!fulfillmentId) {
      setGlobalError(window.orderTrackerTranslations?.errorDisplayError || 'Unable to update this shipment');
      return;
    }
    body = {
      action: 'updateShipmentShippingAddress',
      fulfillmentId: String(fulfillmentId),
      shippingAddress,
    };
  } else {
    const salesOrderId = orderContext.orderSummary?.orderInternalId;
    if (!salesOrderId) {
      setGlobalError(window.orderTrackerTranslations?.errorDisplayError || 'Unable to update this order');
      return;
    }
    body = {
      action: 'updateOrderShippingAddress',
      salesOrderId: String(salesOrderId),
      shippingAddress,
    };
  }

  if (btn) btn.disabled = true;
  setAddressUpdateCancelButtonsDisabled(true);
  labelEl?.classList.add('hidden');
  progEl?.classList.remove('hidden');
  setGlobalError('');

  try {
    const res = await fetchWithTimeout(submitUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await readJsonBody(res);
    const result = normalizeSubmitResponse(data);

    if (!res.ok) {
      const msg =
        resolveSubmitErrorMessage(result) ||
        resolveSubmitErrorMessage(data) ||
        `${tr().errorSubmit || 'Error'} (${res.status})`;
      setGlobalError(msg);
      return;
    }

    if (result?.success === false) {
      setGlobalError(resolveSubmitErrorMessage(result));
      return;
    }

    const updatedShippingAddress = mapResponseToShippingAddress(result ?? data, snap);
    document.dispatchEvent(
      new CustomEvent('orderTrackerAddressUpdateSuccess', {
        bubbles: true,
        detail: {
          shippingAddress: updatedShippingAddress,
          fulfillmentDocumentNo: addressUpdateTarget?.fulfillmentDocumentNo || null,
        },
      })
    );
    setGlobalError('');
    setSuccessStepContent(snap);
    showStep('success');

    const scopeLabel = getById('address-update-scope-label');
    if (scopeLabel) showEl(scopeLabel, false);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollSuccessStepIntoView();
      });
    });
  } catch (err) {
    const aborted = err?.name === 'AbortError';
    setGlobalError(aborted ? tr().errorTimeout || '' : tr().errorNetwork || '');
  } finally {
    if (btn) btn.disabled = false;
    setAddressUpdateCancelButtonsDisabled(false);
    labelEl?.classList.remove('hidden');
    progEl?.classList.add('hidden');
  }
}

function onOrderDisplayed(orderData) {
  orderContext = orderData || null;
  updateAddressUpdateLaunchVisibility(orderData);
}

function onOrderHidden() {
  orderContext = null;
  addressUpdateTarget = null;
  const wrap = getById('address-update-launch-wrap');
  if (wrap) wrap.classList.add('hidden');
  hideAddressPanel();
}

function buildCountrySelect() {
  allowedCountryCodesCache = null;
  const sel = getById('address-update-country-select');
  if (!sel) return;
  sel.innerHTML = '';
  allowedCountryEntries().forEach(({ code, label }) => {
    const opt = document.createElement('option');
    opt.value = code;
    opt.textContent = label;
    sel.appendChild(opt);
  });
  sel.addEventListener('change', () => {
    selectedPlaceId = null;
    placesServicesCache = null;
    hideSuggestLists();
    const country = normalizeCountryCode(sel.value || getDefaultCountryCode());
    updatePhonePrefix(country);
    const phoneInput = getById('address-update-phone');
    if (phoneInput instanceof HTMLInputElement && phoneInput.value) {
      applyPhoneMaskToInput(phoneInput);
    }
  });
}

function bindOnce() {
  if (listenersBound) return;
  listenersBound = true;

  getById('address-update-trigger')?.addEventListener('click', () => showAddressPanel());

  getTrackingRoot()?.addEventListener('click', (e) => {
    const btn = e.target instanceof Element ? e.target.closest('.shipment__update-address-btn') : null;
    if (!btn) return;
    const docNo = btn.getAttribute('data-fulfillment-document-no');
    if (!docNo) return;
    showAddressPanel({ fulfillmentDocumentNo: docNo });
  });

  getById('address-update-cancel-form')?.addEventListener('click', () => hideAddressPanel());
  getById('address-update-done')?.addEventListener('click', () => hideAddressPanel());
  getById('change-delivery-address-discard-changes')?.addEventListener('click', discardChangeDeliveryAddressChanges);

  getById('address-update-submit')?.addEventListener('click', () => {
    if (isEmbeddedAddressMode()) return;
    setGlobalError('');
    if (!validateForm()) return;
    submitUpdate();
  });

  getById('address-update-phone')?.addEventListener('input', (e) => {
    if (e.target instanceof HTMLInputElement) applyPhoneMaskToInput(e.target);
  });

  getById('address-update-phone')?.addEventListener('blur', (e) => {
    if (e.target instanceof HTMLInputElement) applyPhoneMaskToInput(e.target);
  });

  getById('address-update-postal')?.addEventListener('blur', (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement)) return;
    const country = normalizeCountryCode(getById('address-update-country-select')?.value || getDefaultCountryCode());
    input.value = formatPostalCode(country, input.value);
  });

  const addressSearch = getById('address-update-address-search');
  addressSearch?.addEventListener('input', () => {
    selectedPlaceId = null;
    clearTimeout(addressLookupTimer);
    const q = addressSearch.value.trim();
    const ul = getById('address-update-address-suggest');
    if (q.length < 2) {
      showEl(ul, false);
      if (ul) ul.innerHTML = '';
      return;
    }
    addressLookupTimer = setTimeout(async () => {
      try {
        const preds = await runAddressLookup(q);
        const strings = preds.map((p) => ({
          text: predictionDescription(p),
          placeId: predictionPlaceId(p),
        })).filter((x) => x.text && x.placeId);
        renderSuggestList(ul, strings.map((s) => s.text), async (text, index) => {
          const picked = strings[index];
          addressSearch.value = text || '';
          hideSuggestLists();
          if (picked?.placeId) {
            const d = await runAddressDetails(picked.placeId);
            if (!d) return;
            if (!isAllowedCountryCode(d.country)) {
              selectedPlaceId = null;
              setGlobalError(tr().errorCountryNotAllowed || '');
              return;
            }
            selectedPlaceId = picked.placeId;
            applyDetailsToForm(d);
          }
        });
      } catch {
        showEl(ul, false);
      }
    }, DEBOUNCE_MS);
  });

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Node)) return;
    const panel = getAddressPanel();
    if (!panel?.contains(t)) return;
    if (t.closest('#address-update-address-suggest')) return;
    if (
      t.closest('#address-update-address-search') ||
      t.closest('#address-update-email')
    ) {
      return;
    }
    hideSuggestLists();
  });

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Node)) return;

    if (t.closest('.js-open-confirmation-box')) {
      const trigger = t.closest('[data-confirmation-box]');
      const confirmationBoxId = trigger?.dataset?.confirmationBox;

      if (confirmationBoxId === 'cancel-change-delivery-address' && isEmbeddedAddressMode()) {
        return;
      }

      if (confirmationBoxId === 'cancel-change-delivery-address' && !hasFormChanges()) {
        hideAddressPanel();
        return;
      }

      if (confirmationBoxId) {
        const confirmationBox = document.querySelector(`.notification-box[data-confirmation-box="${confirmationBoxId}"]`);

        if (confirmationBox) {
          confirmationBox.classList.add('notification-box--active');
        }
      }
    }

    if (t.closest('.js-close-confirmation-box')) {
      const notificationBox = t.closest('.notification-box');
      if (notificationBox) {
        notificationBox.classList.remove('notification-box--active');
      }
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hideSuggestLists();
  });
}

function init() {
  buildCountrySelect();
  bindOnce();
  scheduleGooglePlacesWarmup();
}

window.OrderTrackerAddressUpdate = {
  onOrderDisplayed,
  onOrderHidden,
  showAddressPanel,
  hideAddressPanel,
  hasEditableShipments,
  validateForm,
  getFormSnapshot,
  getShipToPayload,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
